import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from 'store/middleware/api/api';
import { IPriceBook, IPriceBookItem, IPriceBookState } from 'src/common/interfaces/price-book.interfaces';
import { CostType } from 'src/enums/cost-setup';
import { toast } from '@oliasoft-open-source/react-ui-library';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { checkForDuplicates } from 'src/store/entities/cost-codes/helpers/check-for-duplicates';
import type { TAppDispatch, TGetState } from 'src/store/store-types';

export const initialPriceBookItem: IPriceBookItem = {
  priceBookId: '',
  priceBookItemId: null,
  companyId: null,
  name: 'New Item',
  vendor: 'Vendor',
  price: 100,
  currency: 'USD',
  priceType: CostType.DayRate,
  costCodesId: null,
};

const initialState: IPriceBookState = {
  isAdding: false,
  isFetching: false,
  isUpdating: false,
  priceBooks: [],
  priceBookItems: [],
  priceBookCompanyItems: [],
  error: null,
};

const priceBook = createSlice({
  name: 'priceBook',
  initialState,
  reducers: {
    addPriceBookRequested: (priceBook) => {
      priceBook.isAdding = true;
    },
    addPriceBookReceived: (priceBook, action: PayloadAction<{ priceBook: IPriceBook }>) => {
      priceBook.isAdding = false;
      priceBook.priceBooks.push(action.payload.priceBook);
    },
    addPriceBookRequestedFailed: (priceBook) => {
      priceBook.isAdding = false;
    },
    getPriceBooksRequested: (priceBook) => {
      priceBook.isFetching = true;
    },
    getPriceBooksReceived: (priceBook, action: PayloadAction<{ priceBooks: IPriceBook[] }>) => {
      priceBook.priceBooks = action.payload.priceBooks;
      priceBook.isFetching = false;
    },
    getPriceBookItemsReceived: (priceBook, action: PayloadAction<{ priceBookItems: IPriceBookItem[] }>) => {
      priceBook.priceBookItems = action.payload.priceBookItems;
      priceBook.isFetching = false;
    },
    getPriceBooksRequestedFailed: (priceBook) => {
      priceBook.isFetching = false;
    },
    addPriceBookItemReceived: (priceBook, action: PayloadAction<{ priceBookItem: IPriceBookItem }>) => {
      priceBook.isAdding = false;
      priceBook.priceBookItems.push(action.payload.priceBookItem);
    },
    priceBookItemCellValueUpdated: (
      priceBook,
      action: PayloadAction<{
        id: string;
        value: string;
        field: keyof IPriceBookItem;
      }>,
    ) => {
      const { id, value, field } = action.payload;
      const priceBookItemIndex = priceBook.priceBookItems.findIndex((item) => item.priceBookItemId === id);
      if (priceBookItemIndex !== -1) {
        priceBook.priceBookItems[priceBookItemIndex] = {
          ...priceBook.priceBookItems[priceBookItemIndex],
          [field]: value,
        };
      }
    },
    updatePriceBookRequested: (priceBook) => {
      priceBook.isUpdating = true;
    },
    updatePriceBookReceived: (priceBook, action: PayloadAction<{ priceBook: IPriceBook }>) => {
      if (action.payload?.priceBook) {
        const { priceBookId, name } = action.payload?.priceBook;
        const priceBookIdx = priceBook.priceBooks.findIndex((item) => item.priceBookId === priceBookId);
        priceBook.priceBooks[priceBookIdx].name = name;
      }
      priceBook.isUpdating = false;
    },
    updatePriceBookItemReceived: (priceBook) => {
      priceBook.isUpdating = false;
    },
    updatePriceBookFailed: (priceBook) => {
      priceBook.isUpdating = false;
    },
    priceBookRemoved: (priceBook, action: PayloadAction<{ priceBookId: string }>) => {
      const { priceBookId } = action.payload;
      priceBook.priceBooks = priceBook.priceBooks.filter((item) => item.priceBookId !== priceBookId);
      priceBook.priceBookItems = [];
    },
    priceBookItemRemoved: (priceBook, action: PayloadAction<{ priceBookItemId: string }>) => {
      const { priceBookItemId } = action.payload;
      priceBook.priceBookItems = priceBook.priceBookItems.filter((item) => item.priceBookItemId !== priceBookItemId);
    },
    importPriceBookReceived: (priceBook, action: PayloadAction<{ priceBookItems: IPriceBookItem[] }>) => {
      priceBook.isAdding = false;
      priceBook.priceBookItems = action.payload.priceBookItems;
    },
    importPriceBookFailed: (priceBook, action) => {
      priceBook.isAdding = false;
      priceBook.error = action.payload;
    },
    getPriceBookCompanyItemsReceived: (priceBook, action) => {
      priceBook.priceBookCompanyItems = action.payload.priceBookItems;
      priceBook.isFetching = false;
    },
    clearPriceBook: (priceBook) => {
      priceBook.priceBooks = [];
      priceBook.priceBookItems = [];
      priceBook.priceBookCompanyItems = [];
      priceBook.error = null;
    },
  },
});

export const {
  addPriceBookRequested,
  addPriceBookReceived,
  addPriceBookItemReceived,
  addPriceBookRequestedFailed,
  getPriceBooksRequested,
  getPriceBooksReceived,
  getPriceBookItemsReceived,
  getPriceBooksRequestedFailed,
  priceBookItemCellValueUpdated,
  getPriceBookCompanyItemsReceived,
  updatePriceBookRequested,
  updatePriceBookReceived,
  updatePriceBookItemReceived,
  updatePriceBookFailed,
  priceBookRemoved,
  priceBookItemRemoved,
  importPriceBookReceived,
  importPriceBookFailed,
  clearPriceBook,
} = priceBook.actions;

export default priceBook.reducer;

export const getPriceBooks = () => {
  return apiCallBegan({
    url: '/api/price-books',
    method: 'GET',
    onStart: getPriceBooksRequested.type,
    onSuccess: getPriceBooksReceived.type,
    onError: getPriceBooksRequestedFailed.type,
  });
};

export const addPriceBook = (name: string, companyId: string, subjectId: string) => {
  return apiCallBegan({
    url: '/api/price-book',
    method: 'POST',
    data: { name, companyId, subjectId },
    onStart: addPriceBookRequested.type,
    onSuccess: addPriceBookReceived.type,
    onError: addPriceBookRequestedFailed.type,
  });
};

export const removePriceBook = (id: string) => {
  return apiCallBegan({
    url: `/api/price-book/${id}`,
    method: 'DELETE',
    onSuccess: priceBookRemoved.type,
  });
};

export const updatePriceBook = (data: IPriceBook) => (dispatch: TAppDispatch, getState: TGetState) => {
  const state = getState();
  const { priceBooks } = state.entities.priceBook;

  if (checkNameDuplicates(priceBooks, data)) {
    toast({
      message: {
        type: 'Error',
        icon: true,
        content: i18n.t(translations.priceBook_priceBookCantHaveDuplicateItems) as string,
      },
    });
    return;
  }

  dispatch(
    apiCallBegan({
      url: `/api/price-book`,
      method: 'POST',
      data,
      onStart: updatePriceBookRequested.type,
      onSuccess: updatePriceBookReceived.type,
      onError: updatePriceBookFailed.type,
    }),
  );
};

export const getPriceBookItems = (priceBookId: string) => {
  return apiCallBegan({
    url: `/api/price-book/${priceBookId}`,
    method: 'GET',
    onStart: getPriceBooksRequested.type,
    onSuccess: getPriceBookItemsReceived.type,
    onError: getPriceBooksRequestedFailed.type,
  });
};

export const addPriceBookItem = (data: IPriceBookItem) => {
  return apiCallBegan({
    url: '/api/price-book-item',
    method: 'POST',
    data,
    onStart: addPriceBookRequested.type,
    onSuccess: addPriceBookItemReceived.type,
    onError: addPriceBookRequestedFailed.type,
  });
};

export const removePriceBookItem = (priceBookItemId: string) => {
  return apiCallBegan({
    url: `/api/price-book-item/${priceBookItemId}`,
    method: 'DELETE',
    onSuccess: priceBookItemRemoved.type,
  });
};

export const updatePriceBookItem = (data: IPriceBookItem) => (dispatch: TAppDispatch, getState: TGetState) => {
  const state = getState();
  const items = state.entities.priceBook.priceBookItems;

  if (checkForDuplicates(items, (item) => `${item.name}:${item.vendor}`).hasDuplicate) {
    toast({
      message: {
        type: 'Error',
        icon: true,
        content: i18n.t(translations.priceBook_priceBookCantHaveDuplicateItems) as string,
      },
    });
    return;
  }

  dispatch(
    apiCallBegan({
      url: `/api/price-book-item`,
      method: 'POST',
      data,
      onStart: updatePriceBookRequested.type,
      onSuccess: updatePriceBookItemReceived.type,
      onError: updatePriceBookFailed.type,
    }),
  );
};

export const checkNameDuplicates = (list: IPriceBook[], data: IPriceBook) => {
  return list.some((item) => item.name === data.name);
};

/**
 * Import Price Book
 *
 */
export const importPriceBook = (priceBookId: string, data: IPriceBookItem[]) => {
  return apiCallBegan({
    url: `/api/price-book/import/${priceBookId}`,
    method: 'POST',
    data,
    onStart: addPriceBookRequested.type,
    onSuccess: (response) => {
      toast({
        message: {
          type: 'Success',
          content: i18n.t(translations.successfullImported) as string,
        },
      });
      return {
        type: importPriceBookReceived.type,
        payload: response,
      };
    },
    onError: (error) => {
      return {
        type: importPriceBookFailed.type,
        payload: error,
      };
    },
  });
};

export const getPriceBookCompanyItems = () => {
  return apiCallBegan({
    url: '/api/price-book-items',
    method: 'GET',
    onStart: getPriceBooksRequested.type,
    onSuccess: getPriceBookCompanyItemsReceived.type,
    onError: getPriceBooksRequestedFailed.type,
  });
};
